import { emptyOrderValuesFor, type OrderFormValues } from '@/components/santa-video/order-form-body'
import { BOOK_MAX_ENTRIES, BOOK_MIN_PERSONAL_DETAILS, minInterestsFor } from '@/server/domain/santa-video/book-order-form'
import { MAX_ENTRIES, MAX_LENGTH, MIN_PERSONAL_DETAILS } from '@/server/domain/santa-video/order-form'
import type { VideoStoryId } from '@/server/domain/santa-video/stories'

/**
 * El borrador del wizard, para que quien cancela en Stripe vuelva a `/crear`
 * con todo relleno. `sessionStorage` (no `localStorage`): son datos de un
 * menor y deben morir con la pestaña. Se borra al recibir la `checkoutUrl`.
 *
 * Todo acceso va en `try/catch`: Safari privado o el storage bloqueado lanzan,
 * y el formulario tiene que funcionar igual. El consentimiento NO se guarda.
 * La `v1` de la clave se sube si cambia la forma de `OrderFormValues`.
 *
 * Cada historia tiene su clave: quien rellena el libro y luego la carta (o al
 * revés) no se encuentra el borrador de la otra.
 */
export const DRAFT_KEY = 'video-order-draft-v1'
export const BOOK_DRAFT_KEY = 'video-order-draft-book-v1'

type TextKey = 'name' | 'age' | 'city' | 'parentMessage' | 'email' | 'challenge'
type ListKey = 'personalDetails' | 'authorizedGifts' | 'requestedGifts' | 'interests'
const TEXT_KEYS: readonly TextKey[] = ['name', 'age', 'city', 'parentMessage', 'email', 'challenge']
const LIST_KEYS: readonly ListKey[] = ['personalDetails', 'authorizedGifts', 'requestedGifts', 'interests']

/** Lo que cambia entre historias: la clave, los topes de cada lista y los huecos con los que nace. */
interface DraftShape {
  key: string
  maxEntries: Record<ListKey, number>
  minEntries: Partial<Record<ListKey, number>>
}

const DRAFT_SHAPES: Record<VideoStoryId, DraftShape> = {
  letter: { key: DRAFT_KEY, maxEntries: MAX_ENTRIES, minEntries: { personalDetails: MIN_PERSONAL_DETAILS } },
  book: {
    key: BOOK_DRAFT_KEY,
    maxEntries: { ...BOOK_MAX_ENTRIES, authorizedGifts: 0, requestedGifts: 0 },
    minEntries: { personalDetails: BOOK_MIN_PERSONAL_DETAILS, interests: minInterestsFor(undefined) },
  },
}

/**
 * Los topes del dominio, para que un borrador manipulado o de otra versión no
 * pinte más filas ni más texto de lo que el servidor acepta (el pedido volvería
 * con un 400). `age` y `email` no tienen tope de longitud en el contrato: el
 * primero lo valida `validateStep` y el segundo el servidor.
 */
const TEXT_MAX: Partial<Record<TextKey, number>> = {
  name: MAX_LENGTH.name, city: MAX_LENGTH.city, parentMessage: MAX_LENGTH.parentMessage, challenge: MAX_LENGTH.listEntry,
}

function storage(): Storage | null {
  try {
    return window.sessionStorage
  } catch {
    return null
  }
}

function readText(value: unknown, max: number | undefined): string | undefined {
  if (typeof value !== 'string') return undefined
  return max === undefined ? value : value.slice(0, max)
}

function readList(value: unknown, fallback: string[], maxEntries: number): string[] {
  if (!Array.isArray(value)) return fallback
  const entries = value
    .filter((entry): entry is string => typeof entry === 'string')
    .slice(0, maxEntries)
    .map((entry) => entry.slice(0, MAX_LENGTH.listEntry))
  return entries.length > 0 ? entries : fallback
}

function padTo(entries: string[], min: number): string[] {
  return entries.length >= min ? entries : [...entries, ...Array<string>(min - entries.length).fill('')]
}

/** Lo que haya en el storage, campo a campo: lo que no tiene la forma esperada se queda vacío. */
export function normalizeDraft(raw: unknown, story: VideoStoryId): OrderFormValues {
  const empty = emptyOrderValuesFor(story)
  if (!raw || typeof raw !== 'object') return empty
  const { maxEntries, minEntries } = DRAFT_SHAPES[story]
  const draft = raw as Record<string, unknown>
  const values: OrderFormValues = { ...empty }
  for (const key of TEXT_KEYS) values[key] = readText(draft[key], TEXT_MAX[key]) ?? values[key]
  for (const key of LIST_KEYS) values[key] = padTo(readList(draft[key], empty[key], maxEntries[key]), minEntries[key] ?? 0)
  return values
}

export function loadDraft(story: VideoStoryId): OrderFormValues {
  try {
    const raw = storage()?.getItem(DRAFT_SHAPES[story].key)
    return raw ? normalizeDraft(JSON.parse(raw), story) : emptyOrderValuesFor(story)
  } catch {
    return emptyOrderValuesFor(story)
  }
}

export function saveDraft(values: OrderFormValues, story: VideoStoryId): void {
  try {
    storage()?.setItem(DRAFT_SHAPES[story].key, JSON.stringify(values))
  } catch {
    // Sin storage el borrador no es más que una comodidad perdida.
  }
}

/** Los de todas las historias: quien lo llama (la entrega) no sabe de cuál venía el pedido. */
export function clearDraft(): void {
  try {
    for (const { key } of Object.values(DRAFT_SHAPES)) storage()?.removeItem(key)
  } catch {
    // Ídem.
  }
}
